/**
 * Shared formatting for task/board activity feeds.
 *
 * Extracted from task-detail-panel so both the task drawer and the board
 * Overview activity feed render identical wording. `activityVerb` takes a
 * structural shape so both `TaskActivity` and a board activity task-event
 * (which carries the same type/field/from/to fields) can be passed.
 */

export const ACTIVITY_FIELD_LABEL: Record<string, string> = {
  status: 'status',
  assignee: 'assignee',
  priority: 'priority',
  type: 'type',
  title: 'title',
  description: 'description',
  sprint: 'sprint',
  dueDate: 'due date',
  labels: 'labels',
  storyPoints: 'story points',
  tags: 'tags',
};

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface ActivityVerbInput {
  type: string;
  field?: string | null;
  fromValue?: string | null;
  toValue?: string | null;
}

/**
 * Same wording as `activityVerb`, but for feeds that already name the task next
 * to the verb (the board Activity tab renders "<task> — <verb>"). Saying "created
 * this task" there would repeat the subject.
 */
export function activityVerbShort(entry: ActivityVerbInput): string {
  if (entry.type === 'created') return 'created';
  if (entry.type === 'deleted') return 'deleted';
  return activityVerb(entry);
}

export function activityVerb(entry: ActivityVerbInput): string {
  if (entry.type === 'created') return 'created this task';
  if (entry.type === 'deleted') return 'deleted this task';
  const label = ACTIVITY_FIELD_LABEL[entry.field ?? ''] ?? entry.field ?? 'a field';
  switch (entry.field) {
    case 'description':
      return 'edited the description';
    case 'title':
      return 'renamed the task';
    case 'assignee':
      return entry.toValue ? `assigned to ${entry.toValue}` : 'unassigned the task';
    case 'sprint':
      return entry.toValue ? `moved to sprint "${entry.toValue}"` : 'removed from sprint';
    case 'dueDate':
      return entry.toValue ? `set due date to ${entry.toValue}` : 'cleared the due date';
    case 'labels':
    case 'tags':
      return `updated ${label}`;
    default:
      if (entry.fromValue && entry.toValue) {
        return `changed ${label} from “${entry.fromValue}” to “${entry.toValue}”`;
      }
      if (entry.toValue) return `set ${label} to “${entry.toValue}”`;
      return `changed ${label}`;
  }
}
